/**
 * @param {AudioContext} ctx
 * @param {AudioBuffer} input
 * @returns {AudioBuffer}
 */
export function reverseAudioBuffer(ctx, input) {
  const len = input.length;
  const ch = input.numberOfChannels;
  const rate = input.sampleRate;
  const out = ctx.createBuffer(ch, len, rate);
  for (let c = 0; c < ch; c++) {
    const src = input.getChannelData(c);
    const dst = out.getChannelData(c);
    for (let i = 0; i < len; i += 1) {
      dst[i] = src[len - 1 - i];
    }
  }
  return out;
}

/** @param {Blob} blob */
export async function decodeBlobToAudioBuffer(ctx, blob) {
  const ab = await blob.arrayBuffer();
  return ctx.decodeAudioData(ab.slice(0));
}

/**
 * Reversed buffer over `wallSec` with playback rate ramping up (slow → fast).
 * Mean rate is buffer.duration / wallSec so the clip ends exactly at wallSec; pitch
 * rises with rate like an analog tape coming up to speed.
 * @param {AudioNode} [dest] Defaults to `ctx.destination` (pass a shared SFX bus for level staging).
 * @returns {AudioBufferSourceNode}
 */
export function playBufferTapeRewind(ctx, buffer, wallSec, onEnded, dest) {
  const out = dest ?? ctx.destination;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(out);

  const duration = buffer.duration;
  const rMean = duration / wallSec;
  const minRate = 0.0625;
  /** Start/end multipliers; average must stay 1 so ∫rate dt = duration. */
  const lowMul = 0.28;
  const highMul = 2 - lowMul;

  let r0 = rMean * lowMul;
  let r1 = rMean * highMul;
  if (r0 < minRate) {
    r0 = minRate;
    r1 = 2 * rMean - r0;
  }

  const t0 = ctx.currentTime;
  const t1 = t0 + wallSec;

  const pr = src.playbackRate;
  pr.cancelScheduledValues(t0);
  if (!Number.isFinite(rMean) || wallSec <= 0 || r1 <= r0) {
    pr.setValueAtTime(Math.max(minRate, rMean), t0);
  } else {
    pr.setValueAtTime(r0, t0);
    pr.linearRampToValueAtTime(r1, t1);
  }

  if (onEnded) {
    src.onended = onEnded;
  }
  src.start(t0);
  /* Cap real time on the audio timeline so `onended` always fires (automation / rounding / edge cases). */
  try {
    src.stop(t0 + wallSec + 0.12);
  } catch {
    /* already stopped (e.g. zero-length buffer) */
  }
  return src;
}

/** True for iPhone/iPad (incl. iPadOS desktop UA). */
export function isIosLikeDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * Best MediaRecorder mime for this browser. Safari/iOS records as MP4/AAC, not WebM.
 * @returns {string} mime or '' if MediaRecorder unsupported / nothing supported
 */
export function pickRecorderMimeType() {
  const iosFirst = [
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/aac',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
  ];
  const defaultOrder = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/aac',
    'audio/ogg;codecs=opus',
  ];
  const types = isIosLikeDevice() ? iosFirst : defaultOrder;
  if (typeof MediaRecorder === 'undefined') return '';
  for (const t of types) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return '';
}
